import {useEffect, useState} from 'react';
import {Box, CircularProgress, Paper, TableContainer} from '@mui/material';
import {TaxHeader, PpeAdditionTable} from '@/pages/components/tax-computation/sub';
import {useCompanyAnnualStore, useTaxStore} from '@/store/audit';
import type {TaxPpeDetailsVO} from '@/services/auditService/interface/tax-com';

interface TaxSubP3Props {
    annualId: number;
    p13Data?: TaxPpeDetailsVO | null;
}

export function TaxSubP3({annualId, p13Data: p13DataProp}: TaxSubP3Props) {
    const getTaxComputationInfo = useCompanyAnnualStore((state) => state.getTaxComputationInfo);
    const getCompanyDetailsInfo = useCompanyAnnualStore((state) => state.getCompanyDetailsInfo);
    const initialize = useTaxStore((state) => state.initialize);
    const storeP13Data = useTaxStore((state) => state.p13Data);
    const ppeData = p13DataProp !== undefined ? p13DataProp : storeP13Data;

    const [loading, setLoading] = useState(true);
    const [taxInfo, setTaxInfo] = useState<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null>(null);

    useEffect(() => {
        const loadHeader = async () => {
            setLoading(true);
            try {
                const info = await getTaxComputationInfo(annualId);
                setTaxInfo(info);

                const companyDetails = await getCompanyDetailsInfo(annualId);
                const companyId = companyDetails?.companyId;

                if (companyId) {
                    await initialize(companyId, annualId);
                } else {
                    console.error('Company ID not found');
                }
            } catch (error) {
                console.error('Failed to load tax computation data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadHeader();
    }, [annualId, getTaxComputationInfo, getCompanyDetailsInfo, initialize]);

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!taxInfo) {
        return <Box sx={{p: 2}}>Failed to load data</Box>;
    }

    const tableConfigs: { key: keyof TaxPpeDetailsVO; title: string }[] = [
        {
            key: 'liftEscalators',
            title: 'Lifts and escalators (electric)',
        },
        {
            key: 'furnitureFixture',
            title: 'Furniture and fixture',
        },
        {
            key: 'computerHardwareSoftware',
            title: 'Computer hardware and software',
        },
        {
            key: 'motorVehicle',
            title: 'Motor Vehicle',
        },
        {
            key: 'machinery',
            title: 'Machineries',
        },
        {
            key: 'equipments',
            title: 'Equipments',
        },
    ];

    return (
        <Box sx={{p: 3}}>
            <TaxHeader
                companyName={taxInfo.companyName}
                fileNo={taxInfo.taxFileNo || ''}
                yearOfAssessment={taxInfo.yearOfAssessment}
            />

            <TableContainer component={Paper}>
            {ppeData && tableConfigs.map((config) => {
                const rows = ppeData[config.key];
                // 如果该列表数据为空，则不渲染
                if (!rows || rows.length === 0) {
                    return null;
                }

                return (
                    <PpeAdditionTable
                        key={config.key}
                        tableTitle={config.title}
                        currency={taxInfo.presentationCurrency}
                        storeRow={rows}
                    />
                );
            })}
            </TableContainer>
        </Box>
    );
}
